import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantsApi, type TenantSummary, ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { LifecycleBadge } from '../components/StatusBadge';

export function DashboardPage() {
  const state = useAuthStore((s) => s.state);
  const me = state.status === 'authenticated' ? state.me : null;

  const [recent, setRecent] = useState<TenantSummary[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await tenantsApi.list();
        if (cancelled) return;
        setRecent(r.tenants.slice(0, 5));
        const c: Record<string, number> = {};
        for (const t of r.tenants) c[t.lifecycle_state] = (c[t.lifecycle_state] ?? 0) + 1;
        setCounts(c);
      } catch (err) {
        if (cancelled) return;
        // Silently fail on the home view; the tenants list page surfaces errors.
        void err;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!me) return null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">
          Welcome back, {me.first_name}.
        </h1>
        {/* must_change_password is now gated at App.tsx — users with the flag
            never reach this page, so no warning banner is needed here. */}
      </div>

      {counts && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(['ACTIVE', 'PENDING', 'SUSPENDED', 'ARCHIVED'] as const).map((state) => (
            <div key={state} className="rounded-2xl bg-surface-2 p-5">
              <p className="text-xs uppercase tracking-wider text-ink-muted">{state.toLowerCase()}</p>
              <p className="font-heading text-3xl font-semibold mt-2">{counts[state] ?? 0}</p>
            </div>
          ))}
        </section>
      )}

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg">Recent tenants</h2>
          <Link
            to="/tenants"
            className="text-xs uppercase tracking-wider text-ink-muted hover:text-ink"
          >
            View all →
          </Link>
        </div>
        {recent === null && <p className="text-sm text-ink-muted">Loading…</p>}
        {recent && recent.length === 0 && (
          <div className="border border-dashed border-surface-3 rounded-xl p-8 text-center space-y-3">
            <p className="text-ink-muted text-sm">No tenants yet.</p>
            <Link
              to="/tenants/new"
              className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-ink hover:bg-primary/90 transition"
            >
              Create the first tenant
            </Link>
          </div>
        )}
        {recent && recent.length > 0 && (
          <ul className="divide-y divide-surface-3">
            {recent.map((t) => (
              <li key={t.id} className="py-3 flex items-center justify-between">
                <Link to={`/tenants/${t.id}`} className="hover:underline underline-offset-4">
                  <p className="font-medium">{t.display_name}</p>
                  <p className="text-xs text-ink-muted font-mono">{t.slug}.auremhealth.dev</p>
                </Link>
                <LifecycleBadge state={t.lifecycle_state} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-2">
        <h2 className="font-heading text-lg">Phase progress</h2>
        <p className="text-sm text-ink-muted">
          Phase 0.3 ships tenant CRUD + lifecycle. Brand + offerings extraction (0.4) and the
          actual D1 provisioning + master-data copy + initial Clinic Admin generation (0.5)
          land in subsequent commits.
        </p>
      </section>
    </div>
  );
}
